import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable, signal, inject } from "@angular/core";
import { rxResource } from "@angular/core/rxjs-interop";
import {
  map,
  Observable,
  Subject,
  of,
  tap,
  catchError,
  throwError,
  finalize,
} from "rxjs";

import { LoggerService } from "@services/logger.service";

import { Agent } from "@models/agent.interface";
import { StorageArea, OrderReason, AgentsListResponse, StorageAreaResponse, OrderReasonResponse, PaginatedResponse, GiftProduct, Customer, CompanyWallet, CompanyWalletPaginatedResponse, DistributedBudgetHistory, OrderHeader, PromoAlertData, PrizeItem } from "@models/agents-dtos.model";
import { ApiResponse } from "@models/base-response.model";

import { mapPaginatedListResponse, mapPaginatedListResponseOr } from '@shared/utils/api-response.utils';

import { environment } from "../../environments/environment";
import { 
  CreateAgentRequest, 
  EditAgentRequest, 
  AssociateAgentProductsRequest, 
  UpdateCustomerRequest, 
  CopyDiscountsRequest,
  CustomerProductDiscountRequest,
  DownloadProductsCsvRequest
} from '../api';
import { CustomerPersonalizedProductItem, CustomerPersonalizedProductsResponse } from "../api/dto/customer-products.dto";
import { DiscountPurseItem, DiscountPurseResponse, DiscountPurseActionResponse, CreateDiscountPurseRequest, UpdateDiscountPurseRequest, WalletHistoryResponse, WalletHistoryQueryParams, WalletHistoryTransactionTypesResponse } from "../api/dto/discount-purse.dto";
import { Product } from "../api/dto/products.dto";



type QueryValue = string | number | boolean;
type QueryFilters = Record<string, QueryValue | QueryValue[]>;

interface ApiMessageResponse {
  message?: string;
  success?: boolean;
}

type Payload = unknown;

@Injectable({
  providedIn: "root",
})

export class AgentsService {
  subjectNotifier: Subject<null> = new Subject<null>();
  agentsVersion = signal(0);

  shouldFetchStorageArea = signal(false);
  shouldFetchOrderReasons = signal(false);

  storageAreaResource = rxResource<StorageArea[], number | null>({
    params: () => (this.shouldFetchStorageArea() ? this.agentsVersion() : null),
    stream: (ctx) => {
      if (ctx.params === null) return of([]);
      return this.getStorageArea().pipe(map(res => res.data?.list || []));
    },
  });

  orderReasonsResource = rxResource<OrderReason[], number | null>({
    params: () =>
      this.shouldFetchOrderReasons() ? this.agentsVersion() : null,
    stream: (ctx) => {
      if (ctx.params === null) return of([]);
      return this.getOrderReasons().pipe(map(res => res.data?.list || []));
    },
  });

  private logger = inject(LoggerService);
  constructor(private http: HttpClient) {}

  /** Clears lazy-fetch flags so rxResources leave any error/stale state. */
  resetSessionState(): void {
    this.shouldFetchStorageArea.set(false);
    this.shouldFetchOrderReasons.set(false);
  }


  getAgentsListPaginated(params: {
    page: number;
    pageSize: number;
    search?: string;
    sortField?: string;
    sortOrder?: "asc" | "desc" | "";
  }): Observable<{ items: Agent[]; total: number }> {
    let httpParams = new HttpParams()
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString());

    if (params.search) {
      httpParams = httpParams.set("input", params.search);
    }

    if (params.sortField && params.sortOrder) {
      httpParams = httpParams.set("sortField", params.sortField);
      httpParams = httpParams.set("sortOrder", params.sortOrder.toUpperCase());
    }

    const requestId = Math.random().toString(36).substring(7);
    this.logger.groupCollapsed(`[AgentsService] Fetch Request ${requestId}`);
    this.logger.log("Params:", params);
    this.logger.groupEnd();

    return this.http
      .get<AgentsListResponse>(`${environment.baseUrl}Agent/GetAgentsByFilters`, {
        params: httpParams,
      })
      .pipe(
        map(mapPaginatedListResponse),
        tap({
          next: () => {
            this.logger.log(`[AgentsService] Request ${requestId} completed successfully`);
          },
          error: (err) => {
            this.logger.error(`[AgentsService] Request ${requestId} failed`, err);
          },
        }),
        catchError((err) => {
          return throwError(() => err);
        }),
        finalize(() => {
          this.logger.debug(`[AgentsService] Request ${requestId} stream finalized (Completed or Canceled)`);
        }),
      );
  }


  getGiftProductsList(page: number, pageResults: number): Observable<ApiResponse<PaginatedResponse<GiftProduct>>> {
    return this.http.get<ApiResponse<PaginatedResponse<GiftProduct>>>(
      environment.baseUrl +
      "GiftProduct?page=" +
      page +
      "&pageResults=" +
      pageResults,
    );
  }

  updateAgentGiftProduct(
    agentId: number,
    productId: number,
    quantity: number,
  ): Observable<ApiResponse<PromoAlertData>> {
    const params = new HttpParams()
      .set("agentId", agentId)
      .set("productId", productId)
      .set("quantity", quantity);
    return this.http.put<ApiResponse<PromoAlertData>>(environment.baseUrl + "GiftProduct" + "?" + params, null);
  }

  getAgentById(agentId: number): Observable<ApiResponse<Agent>> {
    return this.http.get<ApiResponse<Agent>>(environment.baseUrl + "Agent/" + agentId);
  }

  getProductsByNameOrCode(text: string): Observable<ApiResponse<PaginatedResponse<Product>>> {
    return this.http.get<ApiResponse<PaginatedResponse<Product>>>(
      environment.baseUrl +
      "Product/GetProductsByFilters?page=1&pageResults=10&nameORcode=" +
      text
    );
  }

  getGiftProductsByFilters(params: {
    page?: number;
    pageResults?: number;
    nameOrCode?: string;
    categoryIds?: number[];
    subCategoryIds?: number[];
  }): Observable<{ items: any[]; total: number }> {
    let httpParams = new HttpParams()
      .set("page", String(params.page ?? 1))
      .set("pageResults", String(params.pageResults ?? 100));

    const trimmedSearch = params.nameOrCode?.trim();
    if (trimmedSearch) {
      httpParams = httpParams.set("nameOrCode", trimmedSearch);
    }

    const normalizeIds = (ids: number[] | undefined): number[] =>
      (ids ?? [])
        .map((id) => Number.parseInt(String(id), 10))
        .filter((id) => Number.isFinite(id) && id > 0);

    const normalizedCategoryIds = normalizeIds(params.categoryIds);
    if (normalizedCategoryIds.length > 0) {
      normalizedCategoryIds.forEach((id) => {
        httpParams = httpParams.append("categoryIds", String(id));
      });
    }

    const normalizedSubCategoryIds = normalizeIds(params.subCategoryIds);
    if (normalizedSubCategoryIds.length > 0) {
      normalizedSubCategoryIds.forEach((id) => {
        httpParams = httpParams.append("subCategoryIds", String(id));
      });
    }

    return this.http
        .get<ApiResponse<any>>(environment.baseUrl + "GiftProduct/GetProductsByFilter", {
        params: httpParams,
      })
      .pipe(
        map((res) => {
          const data = res?.data as any;
          const items = Array.isArray(data?.list)
            ? data.list
            : Array.isArray(data)
              ? data
              : [];

          return {
            items,
            total: typeof data?.totalCount === "number" ? data.totalCount : items.length,
          };
        }),
      );
  }

  associateProductsToAgent(request: AssociateAgentProductsRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(environment.baseUrl + "GiftProduct", request);
  }

  createAppUser(request: CreateAgentRequest): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(environment.baseUrl + "Agent", request);
  }

  editAgent(
    agentId: number,
    request: EditAgentRequest,
    hasFullManagePermission: boolean,
  ): Observable<ApiResponse<Agent>> {
    const endPoint =
      hasFullManagePermission ? "Agent?id=" : "Agent/areamanager-edit/";
    return this.http.put<ApiResponse<Agent>>(environment.baseUrl + endPoint + agentId, request);
  }

  deleteAgentGiftProduct(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(
      environment.baseUrl + "GiftProduct?giftProductId=" + id
    );
  }

  assignWallet(agentId: number, request: CreateDiscountPurseRequest): Observable<DiscountPurseActionResponse> {
    return this.http.post<DiscountPurseActionResponse>(
      environment.baseUrl + "DiscountPurse/?idAgent=" + agentId,
      request
    );
  }

  updateWallet(agentId: number, request: UpdateDiscountPurseRequest): Observable<DiscountPurseActionResponse> {
    return this.http.put<DiscountPurseActionResponse>(
      environment.baseUrl + "DiscountPurse?idAgent=" + agentId,
      request
    );
  }

  deleteWallet(id: number): Observable<DiscountPurseActionResponse> {
    return this.http.delete<DiscountPurseActionResponse>(
      environment.baseUrl + "DiscountPurse?idPurse=" + id
    );
  }

  getDistributedWalletHistory(id: number): Observable<ApiResponse<PaginatedResponse<DistributedBudgetHistory>>> {
    return this.http.get<ApiResponse<PaginatedResponse<DistributedBudgetHistory>>>(
      environment.baseUrl +
      "DiscountPurse/DistributedDiscountPurse?idPurse=" +
      id
    );
  }

  getWalletValuesHistory(
    id: number,
    params?: WalletHistoryQueryParams,
  ): Observable<WalletHistoryResponse> {
    let httpParams = new HttpParams();
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value != null && value !== "") {
          httpParams = httpParams.set(key, String(value));
        }
      }
    }
    return this.http.get<WalletHistoryResponse>(
      environment.baseUrl + "DiscountPurse/story/" + id,
      { params: httpParams },
    );
  }

  getWalletHistoryTransactionTypes(): Observable<WalletHistoryTransactionTypesResponse> {
    return this.http.get<WalletHistoryTransactionTypesResponse>(
      environment.baseUrl + "DiscountPurse/story/transactionTypes",
    );
  }

  suspendAgent(id: number): Observable<ApiMessageResponse> {
    return this.http.put<ApiMessageResponse>(
      environment.baseUrl + "Agent/SuspendOrReactivateAgent?idAgent=" + id,
      null
    );
  }

  //GenericLists/StorageArea
  getStorageArea(): Observable<StorageAreaResponse> {
    return this.http.get<StorageAreaResponse>(environment.baseUrl + "GenericLists/StorageArea");
  }

  getOrderReasons(): Observable<OrderReasonResponse> {
    return this.http.get<OrderReasonResponse>(environment.baseUrl + "GenericLists/OrderReason");
  }


  getWalletHistoryPaginated(
    agentId: number,
    params: { page: number; pageSize: number },
  ): Observable<{ items: DiscountPurseItem[]; total: number }> {
    const httpParams = new HttpParams()
      .set("idAgent", agentId.toString())
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString());

    return this.http
      .get<DiscountPurseResponse>(environment.baseUrl + "DiscountPurse", { params: httpParams })
      .pipe(
        map((res) => ({
          items: res.data.list || [],
          total: res.data.totalCount || 0,
        })),
      );
  }

  getGiftProductsPaginated(
    agentId: number,
    params: { page: number; pageSize: number },
  ): Observable<{ items: GiftProduct[]; total: number }> {
    const httpParams = new HttpParams()
      .set("agentId", agentId.toString())
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString());

    return this.http
      .get<ApiResponse<PaginatedResponse<GiftProduct>>>(environment.baseUrl + "GiftProduct", { params: httpParams })
      .pipe(
        map(mapPaginatedListResponseOr),
      );
  }

  getWalletExcel(id: number): Observable<ArrayBuffer> {
    return this.http.get(
      environment.baseUrl + "DiscountPurse/export-transactions/" + id,
      {
        responseType: "arraybuffer",
      },
    );
  }

  getWalletOrderList(id: number): Observable<ApiResponse<OrderHeader[]>> {
    return this.http.get<ApiResponse<OrderHeader[]>>(
      environment.baseUrl +
      "Document/GetDocumentHeadsForDiscountPurse" +
      (id ? "?idPurse=" + id : "")
    );
  }

  getGiftOrderList(filters: QueryFilters): Observable<ApiResponse<OrderHeader[]>> {
    const params = new HttpParams({ fromObject: filters as Record<string, string | number | boolean | readonly (string | number | boolean)[]> });
    return this.http.get<ApiResponse<OrderHeader[]>>(environment.baseUrl + "GiftProduct/Orders", {
      params
    });
  }

  getWalletOrderDetails(id: number): Observable<ApiResponse<OrderHeader>> {
    return this.http.get<ApiResponse<OrderHeader>>(
      `${environment.baseUrl}Document/GetDocumentByIdForDiscountPurse?id=${id}`,
    );
  }

  getGiftOrderDetails(id: number): Observable<ApiResponse<OrderHeader>> {
    return this.http.get<ApiResponse<OrderHeader>>(
      `${environment.baseUrl}Document/GetDocumentByIdForDiscountPurse?id=${id}`
    );
  }

  getCustomersByAgentIdPaginated(
    agentId: string,
    params: { page: number; pageSize: number; search?: string },
  ): Observable<{ items: Customer[]; total: number }> {
    let httpParams = new HttpParams()
      .set("id", agentId)
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString())
      .set("_sort", "")
      .set("_order", "");

    if (params.search) {
      httpParams = httpParams.set("input", params.search);
    }

    return this.http
      .get<ApiResponse<PaginatedResponse<Customer>>>(environment.baseUrl + "Customer/CustomersByAgentId", {
        params: httpParams,
      })
      .pipe(
        map(mapPaginatedListResponseOr),
      );
  }


  copyDiscountsFromCustomer(request: CopyDiscountsRequest): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(
      environment.baseUrl +
      "CustomerPersonalizedProduct/CopyDiscountsFromCustomer",
      request
    );
  }


  getAllCustomersPaginated(params: {
    page: number;
    pageSize: number;
    search?: string;
  }): Observable<{ items: Customer[]; total: number }> {
    let httpParams = new HttpParams()
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString());

    if (params.search) {
      httpParams = httpParams.set("input", params.search);
    }

    return this.http
      .get<ApiResponse<PaginatedResponse<Customer>>>(environment.baseUrl + "Customer", { params: httpParams })
      .pipe(
        map(mapPaginatedListResponseOr),
      );
  }


  getCustomerById(id: number): Observable<ApiResponse<Customer>> {
    return this.http.get<ApiResponse<Customer>>(environment.baseUrl + "Customer/" + id);
  }

  updateCustomer(customerId: number, request: UpdateCustomerRequest): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      environment.baseUrl + "Customer/" + customerId,
      request
    );
  }

  setCustomerManualBlock(
    customerId: number,
    sellBlocked: boolean,
    reason?: string,
  ): Observable<ApiResponse<void>> {
    const payload: Record<string, unknown> = { sellBlocked };

    if (reason?.trim()) {
      payload["blockReason"] = reason.trim();
    }

    return this.updateCustomer(customerId, payload as UpdateCustomerRequest);
  }

  getCustomersProductsList(id: number, filterObj: QueryFilters): Observable<CustomerPersonalizedProductsResponse> {
    const cleanedFilters: Record<string, string | number | boolean | readonly (string | number | boolean)[]> = {};
    Object.keys(filterObj).forEach((key) => {
      const value = filterObj[key];
      if (
        value !== null &&
        value !== "" &&
        value !== undefined &&
        value !== false &&
        !(Array.isArray(value) && value.length === 0)
      ) {
        cleanedFilters[key] = Array.isArray(value)
          ? value.filter((v): v is string | number | boolean => v !== null && v !== undefined)
          : value;
      }
    });

    const params = new HttpParams({ fromObject: cleanedFilters }).set("id", String(id));

    return this.http.get<CustomerPersonalizedProductsResponse>(
      environment.baseUrl + "CustomerPersonalizedProduct",
      {
        params,
      },
    );
  }

  getCustomersProductsListPaginated(
    id: number,
    params: {
      page: number;
      pageSize: number;
      search?: string;
      categoryIds?: number[];
      discountFilter?: boolean;
    },
  ): Observable<{ items: CustomerPersonalizedProductItem[]; total: number; isWalletEmpty: boolean }> {
    let httpParams = new HttpParams()
      .set("id", id.toString())
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString());

    if (params.search) {
      httpParams = httpParams.set("nameOrCode", params.search);
    }

    if (params.categoryIds && params.categoryIds.length > 0) {
      params.categoryIds.forEach((catId) => {
        httpParams = httpParams.append("categoryIds", catId.toString());
      });
    }

    if (params.discountFilter) {
      httpParams = httpParams.set("discountFilter", "true");
    }

    return this.http
      .get<CustomerPersonalizedProductsResponse>(environment.baseUrl + "CustomerPersonalizedProduct", {
        params: httpParams,
      })
      .pipe(
        map((res) => ({
          items: res.data.list,
          total: res.data.totalCount,
          isWalletEmpty: res.data.isWalletEmpty ?? false,
        })),
      );
  }

  enablePriceVisibility(id: number, body: unknown) {
    return this.http.put(
      environment.baseUrl + "Customer/SetVisibilityCatalog?idCustomer=" + id,
      body
    );
  }

  addCustomerProductDiscount(body: CustomerProductDiscountRequest): Observable<ApiMessageResponse> {
    return this.http.post<ApiMessageResponse>(
      environment.baseUrl + "CustomerPersonalizedProduct",
      body
    );
  }

  updateCustomerProductDiscount(
    body: CustomerProductDiscountRequest,
    skipLoader: boolean = false,
  ): Observable<ApiMessageResponse> {
    const params = new HttpParams().set("skipLoader", skipLoader);
    return this.http.put<ApiMessageResponse>(
      environment.baseUrl + "CustomerPersonalizedProduct",
      body,
      { params },
    );
  }

  getHeaderDocumentOrderId(id: number): Observable<ApiResponse<OrderHeader>> {
    return this.http.get<ApiResponse<OrderHeader>>(
      environment.baseUrl + "Document/GetDocumentByIdForDiscountPurse?id=" + id
    );
  }

  notifyAboutChangeListAgents() {
    this.subjectNotifier.next(null);
    this.agentsVersion.update((v) => v + 1);
  }

  getProductsExcel(body: DownloadProductsCsvRequest): Observable<string> {
    return this.http.post(
      environment.baseUrl + "CustomerPersonalizedProduct/DownloadCsv",
      body,
      {
        responseType: "text",
      },
    );
  }

  getCompanyWalletOrderListPaginated(
    id: number,
    params: { page: number; pageSize: number },
  ): Observable<{ items: CompanyWallet[]; total: number }> {
    const httpParams = new HttpParams()
      .set("idAreaManager", id.toString())
      .set("page", (params.page + 1).toString())
      .set("pageResults", params.pageSize.toString());

    return this.http
      .get<ApiResponse<CompanyWalletPaginatedResponse>>(environment.baseUrl + "PurseBusiness", { params: httpParams })
      .pipe(
        map((res) => ({
          items: res.data?.items || [],
          total: res.data?.totalCount || 0,
        })),
      );
  }

  getCompanyWallet(id: number): Observable<ApiResponse<CompanyWallet>> {
    return this.http.get<ApiResponse<CompanyWallet>>(environment.baseUrl + "PurseBusiness/" + id);
  }

  createCompanyWallet(request: Payload): Observable<ApiResponse<void>> {
    return this.http.post<ApiResponse<void>>(environment.baseUrl + "PurseBusiness", request);
  }

  editCustomerDiscount(id: number, request: Payload): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      environment.baseUrl +
      "CustomerPersonalizedProduct/customer-discounts/" +
      id,
      request
    );
  }

  updateCompanyWallet(id: number, request: Payload): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(environment.baseUrl + "PurseBusiness/" + id, request);
  }

  deleteCompanyWallet(id: number): Observable<ApiResponse<void>> {
    return this.http.delete<ApiResponse<void>>(environment.baseUrl + "PurseBusiness/" + id);
  }

  getCompanyWalletExcel(id: number): Observable<HttpResponse<ArrayBuffer>> {
    return this.http.get(
      environment.baseUrl + "PurseBusiness/export-transactions/" + id,
      {
        responseType: "arraybuffer",
        observe: "response",
      },
    );
  }

  updateWalletValue(agentId: number, request: Payload): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      environment.baseUrl + "DiscountPurse/increment/" + agentId,
      request
    );
  }

  updateCompanyWalletValue(agentId: number, request: Payload): Observable<ApiResponse<void>> {
    return this.http.put<ApiResponse<void>>(
      environment.baseUrl + "PurseBusiness/increment/" + agentId,
      request
    );
  }
  allocateProductToAgents(payload: Payload): Observable<ApiResponse<PromoAlertData>> {
    const url = `${environment.baseUrl}GiftProduct/DistributedGiftProduct`;
    return this.http.post<ApiResponse<PromoAlertData>>(url, payload);
  }

  getPrizes(customerId: number): Observable<ApiResponse<PrizeItem[]>> {
    const url = `${environment.baseUrl}PrizesCatalog/Requests`;
    const params = { customerId };
    return this.http.get<ApiResponse<PrizeItem[]>>(url, {
      params,
    });
  }

  acceptPrizes(payload: {
    prizeId: number;
    quantity: number;
  }): Observable<ApiResponse<void>> {
    const url = `${environment.baseUrl}PrizesCatalog/Requests/${payload.prizeId}/accept`;

    return this.http.put<ApiResponse<void>>(
      url,
      { quantity: payload.quantity }
    );
  }
  rejectPrizes(prizeId: number): Observable<ApiResponse<void>> {
    const url = `${environment.baseUrl}PrizesCatalog/Requests/${prizeId}/reject`;
    return this.http.put<ApiResponse<void>>(url, null);
  }
}
